// Inline editors for structured JSONB params (department budgets, OKRs,
// compliance deadlines). All three share the same save flow: PATCH a whole
// array/object into `customers.company_parameters` via
// /api/customer/params/update — the JSONB column tolerates arbitrary shapes.

import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../lib/backendUrl";

const API_BASE = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtINR(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Math.abs(Number(n));
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

async function saveParam(customerId, key, value, label) {
  const res = await fetch(`${API_BASE}/customer/params/update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_id: customerId, key, value }),
  });
  if (!res.ok) {
    // Surface the 422 schema-validation message back to the editor so the
    // user can fix the offending row instead of seeing a generic "Save failed".
    let detail = null;
    try {
      const body = await res.json();
      detail = body?.detail || null;
    } catch (_e) { /* no-op */ }
    const err = new Error("save_failed");
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  const body = await res.json();
  try {
    window.dispatchEvent(new CustomEvent("core:param-updated", {
      detail: { key, label: label || key, value },
    }));
  } catch (_e) { /* no-op */ }
  return body;
}

function formatParamError(err) {
  if (!err) return "Save failed";
  if (err.status === 422 && err.detail && typeof err.detail === "object") {
    const errs = err.detail.errors;
    if (Array.isArray(errs)) {
      // dept_budgets -> plain strings; okrs/compliance -> per-row Pydantic dicts.
      const flat = errs.flatMap((e) => {
        if (typeof e === "string") return [e];
        if (e && typeof e === "object") {
          if (Array.isArray(e.errors)) {
            return e.errors.map((ee) => `Row ${(e.index ?? 0) + 1}: ${ee?.msg || JSON.stringify(ee)}`);
          }
          return [JSON.stringify(e)];
        }
        return [String(e)];
      });
      return flat.slice(0, 3).join(" · ");
    }
    if (typeof errs === "string") return errs;
  }
  return "Save failed";
}

// ----------------------------- Department Budgets ---------------------------

const DEFAULT_DEPTS = ["Sales", "Operations", "Tech", "Finance"];

export function DeptBudgetsEditor({ customerId, value, lastUpdatedIso, onSaved, disabled }) {
  // value shape: { Sales: 50000, Operations: 30000, ... }
  const initial = useMemo(() => {
    const v = value || {};
    const out = {};
    DEFAULT_DEPTS.forEach((d) => { out[d] = v[d] ?? ""; });
    // Carry custom depts the user added before.
    Object.keys(v).forEach((k) => { if (!(k in out)) out[k] = v[k] ?? ""; });
    return out;
  }, [value]);
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => setRows(initial), [initial]);

  const updateRow = (dept, amt) => setRows((r) => ({ ...r, [dept]: amt }));
  const addDept = () => {
    const name = window.prompt("Department name:");
    if (!name || !name.trim()) return;
    setRows((r) => ({ ...r, [name.trim()]: "" }));
  };
  const removeDept = (dept) => setRows((r) => {
    const next = { ...r }; delete next[dept]; return next;
  });

  const save = async () => {
    setBusy(true); setError(null);
    try {
      // Strip empties; cast to numbers.
      const cleaned = {};
      Object.entries(rows).forEach(([k, v]) => {
        if (v === "" || v === null) return;
        const n = Number(v);
        if (Number.isFinite(n)) cleaned[k] = n;
      });
      await saveParam(customerId, "dept_budgets", cleaned, "Department budgets");
      if (onSaved) onSaved();
    } catch (e) { setError(formatParamError(e)); }
    setBusy(false);
  };

  const ago = timeAgo(lastUpdatedIso);
  const total = Object.values(rows).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <div data-testid="dept-budgets-editor" data-paramkey="dept_budgets" className="space-y-3">
      <div className="flex items-center justify-between text-[11.5px] text-slate-500">
        <span>Allocate your monthly budget across departments</span>
        {ago && <span data-testid="dept-budgets-updated-ago">Updated {ago}</span>}
      </div>
      <div className="rounded-lg border border-slate-200">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Department</th>
              <th className="px-3 py-2 text-right">Monthly budget (₹)</th>
              <th className="px-3 py-2 text-right">% of total</th>
              <th className="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {Object.keys(rows).map((dept) => {
              const amt = Number(rows[dept] || 0);
              const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
              return (
                <tr key={dept} data-testid={`dept-row-${dept.toLowerCase()}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-[#0F1B2D]">{dept}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      value={rows[dept] ?? ""}
                      onChange={(e) => updateRow(dept, e.target.value)}
                      disabled={disabled || busy}
                      placeholder="0"
                      className="w-32 rounded border border-slate-200 bg-white px-2 py-1 text-right text-[13px] tabular-nums focus:border-blue-500 focus:outline-none"
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{pct}%</td>
                  <td className="px-3 py-2">
                    {!DEFAULT_DEPTS.includes(dept) && (
                      <button type="button" onClick={() => removeDept(dept)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`dept-remove-${dept.toLowerCase()}`}
                        aria-label="Remove department">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
              <td className="px-3 py-2 text-[#0F1B2D]">Total</td>
              <td className="px-3 py-2 text-right tabular-nums text-[#0F1B2D]">{fmtINR(total)}</td>
              <td className="px-3 py-2" />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {error && <div className="text-[12px] text-red-600">{error}</div>}
      <div className="flex items-center justify-between">
        <button type="button" onClick={addDept} disabled={disabled || busy}
          data-testid="dept-add-btn"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">
          <Plus size={12} /> Add department
        </button>
        <button type="button" onClick={save} disabled={disabled || busy}
          data-testid="dept-save-btn"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save budgets
        </button>
      </div>
    </div>
  );
}

// ------------------------------- OKR Editor ---------------------------------

function blankOkr() {
  return { objective: "", kr_text: "", progress_pct: 0, deadline: "" };
}

export function OKREditor({ customerId, value, lastUpdatedIso, onSaved, disabled }) {
  // value shape: [{ objective, kr_text, progress_pct, deadline }]
  const [items, setItems] = useState(Array.isArray(value) && value.length ? value : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => setItems(Array.isArray(value) && value.length ? value : []), [value]);

  const updateItem = (idx, key, val) => setItems((arr) => arr.map((o, i) => i === idx ? { ...o, [key]: val } : o));
  const addItem    = () => setItems((arr) => [...arr, blankOkr()]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const cleaned = items
        .map((o) => ({
          objective: (o.objective || "").trim(),
          kr_text:   (o.kr_text || "").trim(),
          progress_pct: Math.max(0, Math.min(100, Math.round(Number(o.progress_pct) || 0))),
          deadline:  o.deadline || null,
        }))
        .filter((o) => o.objective || o.kr_text);
      await saveParam(customerId, "okrs", cleaned, "OKRs");
      if (onSaved) onSaved();
    } catch (e) { setError(formatParamError(e)); }
    setBusy(false);
  };

  const ago = timeAgo(lastUpdatedIso);
  return (
    <div data-testid="okr-editor" data-paramkey="okrs" className="space-y-3">
      <div className="flex items-center justify-between text-[11.5px] text-slate-500">
        <span>Quarterly objectives & key results</span>
        {ago && <span data-testid="okr-updated-ago">Updated {ago}</span>}
      </div>
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
          No OKRs yet. Click <b>Add OKR</b> to capture your top objective.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((o, i) => (
            <div key={i} data-testid={`okr-row-${i}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                <input
                  className="md:col-span-5 rounded border border-slate-200 px-2 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
                  placeholder="Objective (e.g. Hit ₹2Cr Q4 revenue)"
                  value={o.objective || ""}
                  onChange={(e) => updateItem(i, "objective", e.target.value)}
                  disabled={disabled || busy}
                />
                <input
                  className="md:col-span-4 rounded border border-slate-200 px-2 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none"
                  placeholder="Key result (measurable)"
                  value={o.kr_text || ""}
                  onChange={(e) => updateItem(i, "kr_text", e.target.value)}
                  disabled={disabled || busy}
                />
                <input
                  type="number" min={0} max={100}
                  className="md:col-span-1 rounded border border-slate-200 px-2 py-1.5 text-right text-[13px] tabular-nums focus:border-blue-500 focus:outline-none"
                  placeholder="0"
                  value={o.progress_pct ?? ""}
                  onChange={(e) => updateItem(i, "progress_pct", e.target.value)}
                  disabled={disabled || busy}
                />
                <input
                  type="date"
                  className="md:col-span-2 rounded border border-slate-200 px-2 py-1.5 text-[12.5px] focus:border-blue-500 focus:outline-none"
                  value={o.deadline || ""}
                  onChange={(e) => updateItem(i, "deadline", e.target.value)}
                  disabled={disabled || busy}
                />
              </div>
              {/* Progress bar visualisation */}
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full bg-emerald-500"
                    style={{ width: `${Math.max(0, Math.min(100, Number(o.progress_pct) || 0))}%`, transition: "width 300ms ease" }} />
                </div>
                <span className="text-[11px] font-semibold tabular-nums text-slate-700">{Math.round(Number(o.progress_pct) || 0)}%</span>
                <button type="button" onClick={() => removeItem(i)}
                  data-testid={`okr-remove-${i}`}
                  className="text-slate-400 hover:text-red-600">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="text-[12px] text-red-600">{error}</div>}
      <div className="flex items-center justify-between">
        <button type="button" onClick={addItem} disabled={disabled || busy}
          data-testid="okr-add-btn"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">
          <Plus size={12} /> Add OKR
        </button>
        <button type="button" onClick={save} disabled={disabled || busy}
          data-testid="okr-save-btn"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save OKRs
        </button>
      </div>
    </div>
  );
}

// ----------------------- Compliance Deadlines Editor ------------------------

const STATUS_OPTIONS = [
  { value: "pending",   label: "Pending"   },
  { value: "filed",     label: "Filed"     },
  { value: "overdue",   label: "Overdue"   },
  { value: "exempt",    label: "Exempt"    },
];

function blankCompliance() {
  return { filing: "", due_date: "", status: "pending", notes: "" };
}

export function ComplianceEditor({ customerId, value, lastUpdatedIso, onSaved, disabled }) {
  const [items, setItems] = useState(Array.isArray(value) && value.length ? value : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => setItems(Array.isArray(value) && value.length ? value : []), [value]);

  const updateItem = (idx, key, val) => setItems((arr) => arr.map((o, i) => i === idx ? { ...o, [key]: val } : o));
  const addItem = () => setItems((arr) => [...arr, blankCompliance()]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const cleaned = items
        .map((c) => ({
          filing:   (c.filing || "").trim(),
          due_date: c.due_date || null,
          status:   STATUS_OPTIONS.some((s) => s.value === c.status) ? c.status : "pending",
          notes:    (c.notes || "").trim(),
        }))
        .filter((c) => c.filing || c.due_date);
      await saveParam(customerId, "compliance_deadlines", cleaned, "Compliance deadlines");
      if (onSaved) onSaved();
    } catch (e) { setError(formatParamError(e)); }
    setBusy(false);
  };

  const ago = timeAgo(lastUpdatedIso);
  return (
    <div data-testid="compliance-editor" data-paramkey="compliance_deadlines" className="space-y-3">
      <div className="flex items-center justify-between text-[11.5px] text-slate-500">
        <span>Filings, KYC renewals, advance-tax dates — never miss one</span>
        {ago && <span data-testid="compliance-updated-ago">Updated {ago}</span>}
      </div>
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
          No compliance items yet. Click <b>Add filing</b> to track GST, ITR, ROC, TDS etc.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Filing</th>
                <th className="px-3 py-2">Due date</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Notes</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => {
                const due = c.due_date ? new Date(c.due_date) : null;
                const overdue = due && due.getTime() < Date.now() && c.status === "pending";
                return (
                  <tr key={i} data-testid={`compliance-row-${i}`} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input className="w-full rounded border border-slate-200 px-2 py-1 text-[12.5px]"
                        value={c.filing || ""} placeholder="e.g. GSTR-3B"
                        onChange={(e) => updateItem(i, "filing", e.target.value)}
                        disabled={disabled || busy} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="date" className="rounded border border-slate-200 px-2 py-1 text-[12.5px]"
                        value={c.due_date || ""}
                        onChange={(e) => updateItem(i, "due_date", e.target.value)}
                        disabled={disabled || busy} />
                    </td>
                    <td className="px-3 py-2">
                      <select className={`rounded border border-slate-200 px-2 py-1 text-[12.5px] ${overdue ? "bg-red-50 text-red-700" : ""}`}
                        value={c.status || "pending"}
                        onChange={(e) => updateItem(i, "status", e.target.value)}
                        disabled={disabled || busy}>
                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      {overdue && <span className="ml-2 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10.5px] font-semibold text-red-700">Overdue</span>}
                    </td>
                    <td className="px-3 py-2">
                      <input className="w-full rounded border border-slate-200 px-2 py-1 text-[12.5px]"
                        value={c.notes || ""} placeholder="Optional"
                        onChange={(e) => updateItem(i, "notes", e.target.value)}
                        disabled={disabled || busy} />
                    </td>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => removeItem(i)}
                        data-testid={`compliance-remove-${i}`}
                        className="text-slate-400 hover:text-red-600">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {error && <div className="text-[12px] text-red-600">{error}</div>}
      <div className="flex items-center justify-between">
        <button type="button" onClick={addItem} disabled={disabled || busy}
          data-testid="compliance-add-btn"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">
          <Plus size={12} /> Add filing
        </button>
        <button type="button" onClick={save} disabled={disabled || busy}
          data-testid="compliance-save-btn"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save filings
        </button>
      </div>
    </div>
  );
}
